import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useDebouncedTerm } from '../../lib/debounce.js';
import {
  type Action,
  Alert,
  Badge,
  backLinkClasses,
  Card,
  cardClasses,
  Icon,
  Modal,
  NotProvided,
  Page,
  PageHeader,
  Pagination,
  Table,
  Toolbar,
  cn,
} from '@evogard/ui';
import { Tabs, TabsList, TabsTrigger } from '@evogard/ui/primitives';
import {
  EXPENSE_PAYMENT_METHOD_LABELS,
  EXPENSE_STAGE_LABELS,
  HOUSE_TIMEZONE,
  closesAutomatically,
  competenceClosingDate,
  format,
  formatDate,
  fromColumn,
  sum,
  type ExpenseStage,
} from '@evogard/domain';
import type {
  Period,
  StatementBalancePart,
  StatementDay,
  StatementEntry,
  StatementPayout,
  StatementPayoutDay,
} from '@evogard/contract';
import { ClosingState } from '../../components/StatementClosing.js';
import { MoneyValue } from '../../components/Values.js';
import { api } from '../../lib/api.js';
import { can, useSessionContext } from '../../lib/session.js';
import {
  AWARD_STALE_MS,
  awardPeriod,
  periodLabel,
  ReceiptMonthStrip,
  receiptLabel,
  refusalOf,
} from './common.js';
import { StatementChart } from './StatementChart.js';
import { StatementConsultantRecalculate } from './StatementConsultantRecalculate.js';
import { StatementEntryPanel } from './StatementEntryPanel.js';

/**
 * O EXTRATO — a movimentação do consultor, mês a mês.
 *
 * ## O que ele é, e o que a Carteira já era
 *
 * A Carteira responde **quanto** entrou na competência, por premiação. Esta
 * tela responde **de onde**: um movimento por boleto pago, com o dia, o
 * associado, a placa e o que aquele boleto pôs no bolso.
 *
 * E ela não calcula nada. As linhas vêm gravadas de `sales_statement_entries`,
 * escritas quando a esteira de boletos fecha. É a diferença entre um relatório
 * e um extrato: derivado, o papel impresso na sexta e o impresso na segunda
 * trazem números diferentes, os dois dizendo "setembro".
 *
 * ## A forma é a de um extrato de banco, e ela substituiu uma tabela
 *
 * Aqui havia seis colunas — data, associado, placa, premiação, pago, entrada —,
 * e ela respondia a pergunta errada. O que a pessoa abre esta tela para ver é
 * **o que entrou**, e numa tabela isso era a última coluna, à direita do valor
 * do boleto, com a mesma largura, a mesma cor e o mesmo peso: o próprio dinheiro
 * e o do associado ficavam indistinguíveis. Medido em 22/09/2026, na frase de
 * quem usou: *"era para aparecer o que entra como se fosse histórico de
 * depósito"*.
 *
 * O que ficou: os movimentos agrupados **por dia**, cada dia com o quanto
 * entrou nele, e cada movimento com o valor à direita — com sinal e em verde
 * quando é maior que zero. O que gerou a entrada desce para a linha de apoio,
 * em cinza.
 *
 * ## O valor do boleto continua à vista, e continua sendo apoio
 *
 * Sem ele, "R$ 18,87" é um número que a pessoa aceita ou não aceita, e não há
 * terceira opção. **A tela não refaz a conta**, porém: multiplicar o pago pelo
 * percentual dá uma divergência de centavos contra o total, porque o
 * arredondamento acontece uma vez, sobre o mês inteiro — e quem visse os dois
 * números concluiria que o sistema está errado.
 *
 * ## O subtotal do dia vem do SERVIDOR
 *
 * `days` chega medido sobre o período inteiro. Somar a página daria, num dia
 * partido entre duas páginas, dois cabeçalhos com metade do valor cada, e as
 * duas metades pareceriam totais do dia. Ver `DayGroup`.
 *
 * ## O mês ABERTO e o FECHADO são duas telas, e a faixa do topo diz qual
 *
 * Aberto, as linhas ainda podem crescer e as premiações por faixa de placas não
 * aparecem — a faixa delas só é final quando o ciclo fecha. Fechado, o mês é
 * história: nada entra, e as premiações de faixa entraram.
 *
 * ## Nada rola na horizontal
 *
 * Era a exceção que o `DESIGN.md` prevê para tabela; sem a tabela, ela deixou de
 * ser necessária. Cada movimento é um bloco de três linhas, e ele cabe na
 * largura do telefone.
 */
export function StatementScreen() {
  const search = useSearch({ from: '/authenticated/sales/statement' });
  const navigate = useNavigate();
  const { data: context } = useSessionContext();
  const tenantId = context?.currentTenant.id;

  const month = (search.month ?? awardPeriod()) as Period;
  const perPage = 50;

  /* A entrada aberta no painel lateral — ver `StatementEntryPanel`. */
  const [opened, setOpened] = useState<StatementEntry | null>(null);

  const canClose = can(context, 'sales.statement', 'fechar');
  const canAny = can(context, 'sales.statement', 'ver_qualquer');
  /*
   * A TABELA de todos os extratos: quem tem `ver_qualquer` e ainda não
   * escolheu ninguém. É o mesmo despacho da Carteira — sem ele, quem
   * administra e não é consultor abria a tela na frase "você não está
   * vinculado", que é verdade sobre ele e não responde o que ele veio ver.
   */
  const listing = canAny && search.consultant === undefined;

  /*
   * A busca mora no campo da barra, e não dentro do `Filters` (20/10/2026): o
   * campo já existe, e escondê-lo atrás de um botão pedia dois cliques para
   * digitar uma placa. O que se digita vai ao endereço com atraso — o endereço
   * continua sendo o link que se manda para quem atende —, e volta à primeira
   * página pelo mesmo motivo do filtro.
   */
  const [term, setTerm] = useState(search.search ?? '');
  const debouncedTerm = useDebouncedTerm(term);
  useEffect(() => {
    if ((search.search ?? '') === debouncedTerm.trim()) return;
    void navigate({
      to: '/sales/statement',
      search: { ...search, search: debouncedTerm.trim() || undefined, page: 1 },
      replace: true,
    });
  }, [debouncedTerm]);
  /* O endereço mudou por fora (voltar, link): o campo acompanha. */
  useEffect(() => {
    if ((search.search ?? '') !== debouncedTerm.trim()) setTerm(search.search ?? '');
  }, [search.search]);

  /*
   * A tabela de todos os extratos — quem tem entrada gravada no mês.
   *
   * Lista própria do extrato, e não `useConsultants`: aquela pede
   * `sales.teams:ver`, e quem recebeu só o extrato de todo mundo cairia num
   * 403 no lugar da tabela.
   */
  const people = useQuery({
    queryKey: ['sales', 'statement-consultants', tenantId, month],
    enabled: canAny,
    staleTime: AWARD_STALE_MS,
    queryFn: async () => {
      const r = await api.sales.statementConsultants({ query: { month } });
      if (r.status !== 200) throw new Error(refusalOf(r, 'os consultores do extrato'));
      return r.body.consultants;
    },
  });

  const query = useQuery({
    queryKey: [
      'sales',
      'statement',
      tenantId,
      month,
      search.search,
      search.consultant,
      search.page,
    ],
    queryFn: async () => {
      const r = await api.sales.statement({
        query: {
          month,
          search: search.search,
          consultant: search.consultant,
          page: search.page,
          perPage,
        },
      });
      if (r.status !== 200) throw new Error(refusalOf(r, 'o extrato'));
      return r.body;
    },
    staleTime: AWARD_STALE_MS,
    /*
     * A troca de base **descarta** o que estava na tela, em vez de mantê-lo
     * enquanto a nova chega. É o mesmo desenho da Carteira: um extrato é de uma
     * pessoa numa praça, e mostrar o da praça anterior sob o nome da nova é a
     * tela afirmando o que o código não cumpre.
     *
     * A troca de MÊS também descarta (29/10/2026): o mês velho sob o botão do
     * novo não mostrava carregamento nenhum, e a tela passa ao esqueleto. A
     * página e a busca continuam segurando a lista enquanto a nova chega.
     */
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[2] === tenantId && previousQuery?.queryKey[3] === month
        ? previous
        : undefined,
  });

  const data = query.data;

  /*
   * O recálculo de UMA pessoa (26/10/2026) mora aqui, e não no modal de
   * Pagamentos: lá os atos são da praça; este é do extrato aberto.
   */
  const recalculate = canClose && !listing && data?.estado === 'medido' && (
    <div className="flex justify-end">
      <StatementConsultantRecalculate
        month={month}
        consultant={data.consultant.erpVolunteerCode}
        name={data.consultant.nome ?? `consultor ${data.consultant.erpVolunteerCode}`}
        closed={data.closing !== null}
      />
    </div>
  );

  return (
    <Page measure="cheia">
      {canAny && !listing && (
        <Link
          to="/sales/statement"
          search={{ month: search.month, page: 1 }}
          className={backLinkClasses()}
        >
          <Icon nome="seta-esquerda" size={16} />
          Todos os extratos
        </Link>
      )}
      <PageHeader
        title={
          data?.estado === 'medido' && data.consultant.ofAnotherConsultant
            ? `Extrato de ${data.consultant.nome ?? `consultor ${data.consultant.erpVolunteerCode}`}`
            : 'Extrato'
        }
      >
        {listing
          ? 'O extrato de cada consultor da base na competência. Abra um para ver as entradas, linha a linha.'
          : undefined}
        {/* O extrato de UMA pessoa não tem frase (29/10/2026, decisão do dono). */}
      </PageHeader>

      {/*
       * O mês é a faixa do Início (29/10/2026; antes, o par "mês atual /
       * próximo"), e não um `Filters`: um painel para escolher o mês escondia
       * a escolha. O consultor saiu junto — quem tem `ver_qualquer` troca de
       * extrato pela tabela, em "Todos os extratos".
       */}
      <ReceiptMonthStrip
        current={month}
        loading={listing ? people.isFetching : query.isFetching}
        onChange={(period) =>
          void navigate({
            to: '/sales/statement',
            /* Mês novo volta à primeira página: a terceira de um mês quase
             * nunca existe no outro. */
            search: { ...search, month: period, page: 1, lot: undefined },
          })
        }
      />

      {!listing && (
        <Toolbar
          search={{
            label: 'Buscar no extrato',
            placeholder: 'Placa ou premiação',
            value: term,
            onChange: setTerm,
          }}
        />
      )}

      {query.isError && <Alert tone="erro">{query.error.message}</Alert>}

      {/*
       * O SELO do fechamento, só em leitura, nos dois estados.
       *
       * Os botões de fechar, recalcular e reabrir saíram daqui em 25/09/2026 e
       * foram para Comercial › Pagamentos Recorrência, ao lado da Janela de envio: o
       * Extrato é o documento de uma pessoa, e o fechamento é um ato da praça
       * inteira. O selo fica no mês ABERTO, porque ele diz que o total abaixo
       * é "por enquanto". No fechado o card saiu (29/10/2026, decisão do dono).
       */}
      {data !== undefined && data.closing === null && (
        <div className={cardClasses({ className: 'flex flex-col gap-3' })}>
          <ClosingState closing={data.closing} month={month} />
          {recalculate}
        </div>
      )}
      {data !== undefined && data.closing !== null && recalculate}

      {/*
       * A recusa vem NO LUGAR do conteúdo, como na Carteira: um extrato zerado
       * embaixo de um aviso diria "você não recebeu nada" logo depois de dizer
       * que não sabe, e a afirmação é a que se lê.
       */}
      {listing && (
        <>
          {people.isError && <Alert tone="erro">{people.error.message}</Alert>}
          <Table
            framed
            paginate={false}
            label="Extratos da competência"
            items={people.data ?? []}
            loading={people.isLoading}
            rowKey={(p) => p.erpVolunteerCode}
            empty={`Nenhum consultor tem entrada gravada em ${periodLabel(month)}.`}
            rowTitle={(p) => p.nome ?? `Consultor ${p.erpVolunteerCode}`}
            rowSubtitle={(p) => `Código ${p.erpVolunteerCode} nesta base`}
            rowIcon="cracha"
            rowActions={(p): Action[] => [
              {
                label: 'Ver o extrato',
                icon: 'olho',
                run: () =>
                  void navigate({
                    to: '/sales/statement',
                    search: {
                      month: search.month,
                      consultant: p.erpVolunteerCode,
                      page: 1,
                    },
                  }),
              },
            ]}
            columns={[
              {
                title: 'Consultor',
                alignment: 'inicio' as const,
                minWidth: '14rem',
                cell: (p) => p.nome ?? <NotProvided>sem nome</NotProvided>,
              },
              { title: 'Código', minWidth: '5.5rem', cell: (p) => p.erpVolunteerCode },
              {
                title: 'Entradas',
                alignment: 'fim' as const,
                minWidth: '6rem',
                cell: (p) => p.entries.toLocaleString('pt-BR'),
              },
              {
                title: 'Total da competência',
                alignment: 'fim' as const,
                minWidth: '9rem',
                cell: (p) => <MoneyValue value={p.total} />,
              },
            ]}
          />
        </>
      )}

      {!listing && query.isPending && !query.isError && <StatementSkeleton />}

      {!listing && data?.estado === 'sem_vinculo' && (
        <Alert tone="alerta">{data.message}</Alert>
      )}

      {!listing && data?.estado === 'medido' && (
        <>
          {/*
           * O total do mês e o SALDO lado a lado, e são perguntas diferentes: o
           * total é o que entrou nesta competência; o saldo é quanto dela a
           * casa ainda deve, e cai quando o Financeiro dá baixa (29/09/2026).
           * Os dois são da competência do filtro desde 20/10/2026 — o saldo de
           * todas as competências é o do topo.
           */}
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <Summary
              label={search.search ? 'Total do filtro' : 'Total da competência'}
              total={data.total}
              support={`${data.totalRows} ${data.totalRows === 1 ? 'entrada' : 'entradas'} em ${periodLabel(month)}`}
              closed={data.closing !== null}
            />
            <BalanceSummary
              month={month}
              parts={data.balanceParts}
              highlight={search.lot}
              onDismissHighlight={() =>
                void navigate({
                  to: '/sales/statement',
                  search: { ...search, lot: undefined },
                  replace: true,
                })
              }
            />
          </div>

          {data.entries.length === 0 && (search.page > 1 || data.payouts.length === 0) ? (
            <Card title="Entradas">
              <NotProvided>
                {search.search
                  ? `Nenhuma entrada de ${periodLabel(month)} casa com "${search.search}".`
                  : `Nenhuma entrada em ${periodLabel(month)}. Ou a competência não teve boleto pago na carteira, ou as premiações dela ainda não foram cadastradas — a tela de BI diz qual dos dois.`}
              </NotProvided>
            </Card>
          ) : (
            <>
              {/*
               * A troca entre a LISTA e o GRÁFICO — as mesmas entradas, duas
               * perguntas: a lista responde "de onde veio cada uma", o gráfico
               * responde "como o mês andou". A vista mora no endereço, como o
               * mês e a busca: é o link que alguém manda para quem confere.
               *
               * O gráfico não pagina: ele desenha `days`, medido sobre o
               * período inteiro. Por isso o paginador só existe na lista.
               */}
              <Tabs
                value={search.view ?? 'lista'}
                onValueChange={(value) =>
                  void navigate({
                    to: '/sales/statement',
                    search: {
                      ...search,
                      view: value === 'grafico' ? 'grafico' : undefined,
                    },
                    replace: true,
                  })
                }
              >
                <TabsList
                  aria-label="Como ver as entradas"
                  className={cn(data.entries.length === 0 && 'hidden')}
                >
                  <TabsTrigger value="lista">Lista</TabsTrigger>
                  <TabsTrigger value="grafico">Gráfico</TabsTrigger>
                </TabsList>
              </Tabs>

              {search.view === 'grafico' ? (
                <StatementChart
                  days={data.days}
                  month={month}
                  filtered={search.search !== undefined}
                />
              ) : (
                <section className={cardClasses({ padding: false })}>
                  {groupsOf(
                    data.days,
                    data.entries,
                    /* As saídas vão na primeira página — ver `groupsOf`. */
                    search.page > 1 ? [] : data.payouts,
                  ).map((group) => (
                    <DayGroup
                      key={group.date ?? 'fecho'}
                      group={group}
                      onOpen={setOpened}
                    />
                  ))}

                  <StatementEntryPanel
                    entry={opened}
                    consultant={search.consultant}
                    onClose={() => setOpened(null)}
                  />

                  <Pagination
                    page={data.page}
                    perPage={data.perPage}
                    total={data.totalRows}
                    onChange={(page) =>
                      void navigate({
                        to: '/sales/statement',
                        search: { ...search, page },
                      })
                    }
                    label="entradas do extrato"
                    className="border-t border-borda-suave px-5 py-4"
                  />
                </section>
              )}
            </>
          )}
        </>
      )}
    </Page>
  );
}

/**
 * Um dia do extrato: as entradas dele que estão nesta página e as saídas —
 * os lotes pagos naquele dia. Um dos dois pode faltar, nunca os dois.
 */
interface DayGroup {
  /** Nulo é o fecho do mês — a premiação por faixa, sem data de boleto. */
  date: string | null;
  day: StatementDay | null;
  entries: StatementEntry[];
  payoutDay: StatementPayoutDay | null;
}

/**
 * As entradas da página, repartidas pelos dias que o servidor mediu, com as
 * SAÍDAS no dia da baixa.
 *
 * A ordem é a das datas — do mais novo para o mais velho, com o grupo do fecho
 * do mês por último —, e não a da lista de entradas: os dois vêm do mesmo
 * `order by`, e depender do segundo faria a tela reordenar sozinha no dia em
 * que a listagem ganhasse outro critério.
 *
 * Dia sem entrada NESTA página some. Ele existe no período — a página seguinte
 * o mostra —, e desenhar um cabeçalho com subtotal e nenhuma linha embaixo
 * pareceria um dia que perdeu os movimentos.
 *
 * ## As saídas não paginam, e vão na primeira página
 *
 * São no máximo dois lotes por competência, e o servidor as manda inteiras.
 * Quem chama passa a lista só na primeira página; aqui elas entram no dia
 * delas, e o dia de saída sem entrada nenhuma vira um grupo próprio, na ordem
 * da data. A troca: um pagamento mais velho que todas as entradas da primeira
 * página aparece no pé dela, e não na página em que as entradas daquele dia
 * estão — dividir as linhas entre páginas pela data exigiria saber onde cada
 * página começa, que é conta do servidor.
 */
function groupsOf(
  days: readonly StatementDay[],
  entries: readonly StatementEntry[],
  payouts: readonly StatementPayoutDay[],
): DayGroup[] {
  const outgoing = new Map(payouts.map((p) => [p.date, p]));
  const groups: DayGroup[] = days
    .map((day) => ({
      date: day.date,
      day,
      entries: entries.filter((e) => e.paidOn === day.date),
      payoutDay: day.date === null ? null : (outgoing.get(day.date) ?? null),
    }))
    .filter((group) => group.entries.length > 0);

  /* O dia de saída cujas entradas não estão nesta página vira grupo próprio. */
  const shown = new Set(groups.map((g) => g.date));
  for (const payoutDay of payouts) {
    if (!shown.has(payoutDay.date)) {
      groups.push({ date: payoutDay.date, day: null, entries: [], payoutDay });
    }
  }

  return groups.sort((a, b) => {
    if (a.date === b.date) return 0;
    if (a.date === null) return 1;
    if (b.date === null) return -1;
    return a.date < b.date ? 1 : -1;
  });
}

/**
 * Um DIA — o cabeçalho com o total e os movimentos dele embaixo.
 *
 * É a forma de extrato bancário, e ela foi escolhida contra a tabela de seis
 * colunas que existia aqui: a pergunta que a tela responde é "o que entrou", e
 * numa tabela o que entrou era a última coluna, à direita de `Pago` — mesma
 * largura, mesma cor, mesmo peso que o valor do boleto ao lado. Quem abria não
 * distinguia o próprio dinheiro do dinheiro do associado.
 *
 * ## O subtotal é do DIA, e não do que coube na página
 *
 * `day.total` vem do servidor, medido sobre o período inteiro. Somado aqui, um
 * dia partido entre duas páginas apareceria duas vezes com metade do valor
 * cada, e as duas metades pareceriam totais — a tela afirmando o que não
 * cumpre. Quando a página tem só parte do dia, o cabeçalho **diz** quantas das
 * entradas dele estão à vista.
 *
 * ## Entrada e saída no mesmo dia são dois números, e não um líquido
 *
 * O cabeçalho mostra o que entrou e o que saiu separados. Somá-los num líquido
 * esconderia os dois fatos que a pessoa veio conferir: quanto ganhou naquele
 * dia e quanto recebeu.
 */
function DayGroup({
  group,
  onOpen,
}: {
  group: DayGroup;
  onOpen: (entry: StatementEntry) => void;
}) {
  const { date, day, entries, payoutDay } = group;

  return (
    <div className="border-b border-borda-suave last:border-0">
      <header className="flex items-center justify-between gap-3 bg-superficie-suave px-3 py-2.5">
        <div className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <h3 className="text-sm font-semibold text-texto">
            {/*
             * A linha de `faixa` não tem data porque ela não é um movimento do
             * mês: é o fecho dele, a premiação direta apurada pelo piso de
             * faixa alcançado. Por isso o grupo tem nome, em vez de um travessão
             * onde a data estaria.
             */}
            {date === null ? 'Fecho do mês' : dayLabel(date)}
          </h3>
          {day !== null && day.rows > entries.length && (
            <span className="text-xs text-texto-suave">
              {entries.length} de {day.rows} nesta página
            </span>
          )}
        </div>
        <span className="flex shrink-0 items-baseline gap-3">
          {payoutDay !== null && <Outgoing value={payoutDay.total} />}
          {day !== null && entries.length > 0 && <Incoming value={day.total} />}
        </span>
      </header>

      <ul>
        {payoutDay?.payouts.map((payout) => (
          <li
            key={`pagamento-${payout.paymentId}`}
            className="group grid min-h-16 grid-cols-[1.25rem_minmax(0,1fr)_auto] items-start gap-x-2 border-b border-borda-suave px-2 py-2.5 last:border-0"
          >
            <Timeline />
            <Payout payout={payout} />
            <Outgoing value={payout.amount} />
          </li>
        ))}
        {entries.map((entry) => (
          <li key={entry.id} className="group border-b border-borda-suave last:border-0">
            {/*
             * A entrada de BOLETO abre o painel lateral — quem pagou, os veículos e
             * os atendimentos (19/10/2026). A de faixa não tem associado nem
             * boleto, e continua sendo só leitura.
             */}
            {entry.boletoCode === null ? (
              <div className="grid min-h-16 grid-cols-[1.25rem_minmax(0,1fr)_auto] items-start gap-x-2 px-2 py-2.5">
                <Timeline />
                <Entry entry={entry} />
                <Incoming value={entry.amount} />
              </div>
            ) : (
              <button
                type="button"
                onClick={() => onOpen(entry)}
                aria-label={`Ver o associado e os atendimentos de ${entry.memberName ?? 'associado sem nome'}`}
                className="grid min-h-16 w-full cursor-pointer grid-cols-[1.25rem_minmax(0,1fr)_auto] items-start gap-x-2 px-2 py-2.5 text-left transition-colors hover:bg-superficie-suave"
              >
                <Timeline />
                <Entry entry={entry} />
                <Incoming value={entry.amount} />
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Uma SAÍDA — o lote que o Financeiro pagou, declarado como pago.
 *
 * O selo "Foi pago" é a afirmação da linha, e ela é verdadeira por construção:
 * o servidor só manda para cá a despesa em `pagamento_efetuado`. Embaixo, de
 * qual lote e competência era — o lote de setembro pago em outubro sai no
 * extrato de outubro, e sem a competência a pessoa não saberia o que foi
 * pago —, e como e quando.
 */
function Payout({ payout }: { payout: StatementPayout }) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <span className="flex flex-wrap items-center gap-2">
        <span className="truncate text-sm font-medium text-texto">
          Pagamento da premiação
        </span>
        <Badge tone="sucesso">Foi pago</Badge>
      </span>
      <span className="truncate text-xs text-texto">
        lote {payout.category} de {periodLabel(payout.lotPeriod as Period)}
      </span>
      <span className="text-xs text-texto-suave">
        {EXPENSE_PAYMENT_METHOD_LABELS[payout.paymentMethod]} · pago em{' '}
        {formatDate(payout.paidOn)}
      </span>
    </div>
  );
}

/**
 * O trilho à esquerda de cada movimento — a linha do dia e o ponto da entrada.
 *
 * É decoração, e por isso `aria-hidden`: o que ele diz ("estas entradas são do
 * mesmo dia") o cabeçalho do grupo já disse em palavras. A linha nasce no ponto
 * da primeira entrada e morre no da última, para o trilho não vazar para fora
 * do dia.
 */
function Timeline() {
  return (
    <span aria-hidden className="relative self-stretch">
      <span className="absolute -inset-y-2.5 left-1/2 w-px -translate-x-1/2 bg-borda group-first:top-2 group-last:bottom-[calc(100%-0.5rem)]" />
      <span className="absolute left-1/2 top-2 size-[5px] -translate-x-1/2 -translate-y-1/2 rounded-pilula bg-texto-suave" />
    </span>
  );
}

/**
 * Uma entrada — a premiação em cima e, embaixo, de onde ela veio. O valor mora
 * na terceira coluna da linha, e fica sempre à vista.
 *
 * As duas espécies trocam o apoio pelo `kind`, nunca por uma suposição sobre o
 * que veio nulo — é a mesma regra do relatório da premiação. A de `faixa` não
 * tem associado, placa nem boleto, e diz isso em palavras em vez de mostrar
 * travessões que o leitor tem que interpretar.
 *
 * O texto trunca e o valor não: numa linha estreita, perder o fim do nome do
 * associado é aceitável; perder o valor é perder a razão da tela.
 *
 * ## O valor do boleto continua à vista, e como APOIO
 *
 * Sem ele, "R$ 18,87" é um número que a pessoa aceita ou não aceita, e não há
 * terceira opção. Com ele em cinza e abaixo, o que se lê primeiro é o que
 * entrou.
 *
 * **A tela não refaz a conta.** Multiplicar o pago pelo percentual dá uma
 * divergência de centavos contra o total, porque o arredondamento acontece uma
 * vez, sobre o mês inteiro — e quem visse os dois números concluiria que o
 * sistema está errado.
 *
 * Não há hora na linha: `paidOn` é data de negócio, e o SGA não diz a que horas
 * a baixa aconteceu. Uma hora ali seria inventada.
 */
function Entry({ entry }: { entry: StatementEntry }) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <span className="truncate text-sm font-medium text-texto">{entry.award.title}</span>

      {entry.kind === 'faixa' ? (
        <span className="truncate text-xs text-texto-suave">
          {entry.plates} {entry.plates === 1 ? 'placa ativada' : 'placas ativadas'} no mês
          {' · '}
          {entry.tierFloor === null
            ? 'abaixo da menor faixa'
            : `faixa de ${entry.tierFloor}`}
        </span>
      ) : (
        <>
          {/*
           * O nome é o que o ERP escrevia QUANDO a linha nasceu, e pode faltar
           * no cadastro. Nulo diz que o espelho não tinha o nome — não que o
           * associado não exista.
           */}
          <span className="truncate text-xs text-texto">
            {entry.memberName ?? 'associado sem nome no espelho'}
          </span>
          {(entry.plate !== null || entry.boletoCode !== null) && (
            <span className="truncate text-xs text-texto-suave">
              {entry.plate !== null && <span className="font-mono">{entry.plate}</span>}
              {entry.plate !== null && entry.boletoCode !== null && ' · '}
              {entry.boletoCode !== null && `boleto nº ${entry.boletoCode}`}
            </span>
          )}
          <span className="truncate text-xs text-texto-suave">
            valor do boleto <MoneyValue value={entry.gross} />
          </span>
          {/* A linha da premiação POR PRODUTO diz por quais produtos ela pagou. */}
          {entry.kind === 'produto' && entry.products && (
            <span className="truncate text-xs text-texto-suave">{entry.products}</span>
          )}
        </>
      )}
    </div>
  );
}

/**
 * O resumo da competência — a pastilha da carteira, o total e quantas entradas.
 *
 * Horizontal e baixo, e não o `StatCard`: é um cartão só, em cima de uma
 * lista, e a forma de fileira do `StatCard` deixava metade dele vazia.
 *
 * O número fica verde só no mês FECHADO. Aberto, ele ainda cresce, e o verde de
 * "está garantido" prometeria um fecho que não houve. A pastilha é verde nos
 * dois: ela diz o assunto — dinheiro —, não o estado, como o `ACCENTS` do
 * `StatCard`.
 */
/**
 * O EXTRATO ainda chegando (29/10/2026) — a forma da tela, sem número: os dois
 * cartões do topo e as linhas das entradas. Sem ele a tela ficava em branco
 * abaixo da faixa de meses até a apuração responder.
 */
function StatementSkeleton() {
  return (
    <div
      className="flex flex-col gap-5"
      aria-busy="true"
      aria-label="Carregando o extrato"
    >
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {[0, 1].map((i) => (
          <div
            key={i}
            className={cardClasses({
              density: 'densa',
              className: 'flex items-center gap-3',
            })}
          >
            <span className="esqueleto block size-10 shrink-0 rounded-grande" />
            <div className="flex flex-1 flex-col gap-2">
              <span className="esqueleto block h-3 w-1/3" />
              <span className="esqueleto block h-5 w-1/2" />
              <span className="esqueleto block h-3 w-2/3" />
            </div>
          </div>
        ))}
      </div>
      <section className={cardClasses({ className: 'flex flex-col gap-4' })}>
        <span className="esqueleto block h-4 w-40" />
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="flex items-center gap-3">
            <span className="esqueleto block size-8 shrink-0 rounded-full" />
            <span className="esqueleto block h-4 flex-1" />
            <span className="esqueleto block h-4 w-24" />
          </div>
        ))}
      </section>
    </div>
  );
}

function Summary({
  label,
  total,
  support,
  closed,
}: {
  label: string;
  total: string;
  support: string;
  closed: boolean;
}) {
  return (
    <div
      className={cardClasses({ density: 'densa', className: 'flex items-center gap-3' })}
    >
      <span className="grid size-10 shrink-0 place-items-center rounded-grande bg-sucesso-suave text-sucesso">
        <Icon nome="dinheiro" size={20} />
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="text-xs text-texto-suave">{label}</span>
        <span
          className={cn(
            'text-lg font-semibold tabular-nums',
            closed ? 'text-sucesso' : 'text-texto',
          )}
        >
          <MoneyValue value={total} />
        </span>
        <span className="truncate text-xs text-texto-suave">{support}</span>
      </div>
      <span aria-hidden className="shrink-0 text-sucesso">
        <Icon nome="grafico" size={20} />
      </span>
    </div>
  );
}

/**
 * Um valor que ENTRA — com o sinal, e verde quando é maior que zero.
 *
 * O verde aqui não é o mesmo verde do total da competência, e a diferença é
 * deliberada: o cartão do topo só fica verde no mês fechado, porque enquanto ele
 * está aberto o total ainda sobe e o verde prometeria um fecho que não houve.
 * Uma entrada é outra coisa — cada linha é um boleto que já foi pago —, e o que
 * o mês aberto não garante é quantas linhas ainda faltam, nunca estas.
 *
 * Zero fica sem sinal e sem cor. É o caso da premiação direta cuja contagem não
 * chegou ao menor degrau: um "+ R$ 0,00" verde diria que alguma coisa entrou.
 */
function Incoming({ value }: { value: string }) {
  const cents = centsOf(value);
  const entered = cents !== null && cents > 0;

  return (
    <span
      className={cn(
        'whitespace-nowrap text-sm font-semibold tabular-nums',
        entered ? 'text-sucesso' : 'text-texto',
      )}
    >
      {entered && '+ '}
      <MoneyValue value={value} />
    </span>
  );
}

/**
 * Um valor que SAI — o pagamento de um lote, com o sinal de menos.
 *
 * Sem vermelho, de propósito: o vermelho desta casa é "algo está errado", e a
 * saída aqui é a melhor notícia da tela — o dinheiro chegou ao consultor. O
 * sinal diz a direção; o selo "Foi pago" da linha diz o fato.
 */
function Outgoing({ value }: { value: string }) {
  return (
    <span className="whitespace-nowrap text-sm font-semibold tabular-nums text-texto">
      − <MoneyValue value={value} />
    </span>
  );
}

/**
 * O saldo em aberto DA COMPETÊNCIA do filtro, no formato do cartão do total.
 *
 * Nunca verde: o saldo é o que AINDA não foi pago, e o verde desta tela é o do
 * dinheiro garantido.
 *
 * **É da competência escolhida, e não de todas** (20/10/2026). Até aqui o
 * cartão mostrava o saldo da pessoa inteira ao lado do total de UM mês, e com o
 * filtro em setembro aparecia o valor de outubro — lido como se fosse do mês.
 * O saldo de todas as competências continua no topo da interna.
 *
 * **Clicar abre a origem**: os lotes da competência (1x e 2x), em que pé está
 * cada um, e as premiações que o compõem. Sem saldo, não há o que abrir.
 */
function BalanceSummary({
  month,
  parts,
  highlight,
  onDismissHighlight,
}: {
  month: Period;
  parts: readonly StatementBalancePart[];
  /**
   * O lote que o "Meu saldo" do topo mandou destacar (20/10/2026). Com ele, o
   * saldo já abre, e o lote aparece marcado.
   */
  highlight: '1x' | '2x' | undefined;
  /** Tira o destaque do endereço ao fechar — recarregar não reabre o modal. */
  onDismissHighlight: () => void;
}) {
  const mine = parts.filter((p) => p.period === month);
  const monthBalance = sum(...mine.map((p) => fromColumn(p.amount)!));
  /*
   * Sem saldo a receber, o card não abre nada (29/10/2026, decisão do dono):
   * um modal que só diria "nada em aberto" é um clique que não leva a lugar
   * nenhum. Vale também para o destaque vindo do "Meu saldo" do topo.
   */
  const openable = mine.length > 0;
  const [open, setOpen] = useState(highlight !== undefined && openable);
  const close = () => {
    setOpen(false);
    if (highlight !== undefined) onDismissHighlight();
  };

  const content = (
    <>
      <span className="grid size-10 shrink-0 place-items-center rounded-grande bg-superficie-suave text-texto-suave">
        <Icon nome="relogio" size={20} />
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="text-xs text-texto-suave">Saldo em aberto</span>
        <span className="text-lg font-semibold tabular-nums text-texto">
          {format(monthBalance)}
        </span>
        <span className="text-xs text-texto-suave">
          {openable
            ? `a receber em ${receiptLabel(month)}, ainda não pago · ver a origem`
            : `nada a receber em ${receiptLabel(month)}`}
        </span>
      </span>
    </>
  );

  if (!openable)
    return (
      <div
        className={cardClasses({
          density: 'densa',
          className: 'flex items-center gap-3',
        })}
      >
        {content}
      </div>
    );

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        className={cardClasses({
          density: 'densa',
          className:
            'flex items-center gap-3 text-left transition-colors hover:bg-superficie-suave focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foco',
        })}
      >
        {content}
        <Icon nome="seta-direita" size={16} />
      </button>

      {open && (
        <Modal
          open
          onOpenChange={(next) => !next && close()}
          title={`Saldo em aberto de ${periodLabel(month)}`}
          size="md"
        >
          <ul className="flex flex-col gap-3">
            {mine.map((part) => (
              <BalancePartItem
                key={part.category}
                part={part}
                highlighted={part.category === highlight}
              />
            ))}
          </ul>
        </Modal>
      )}
    </>
  );
}

/** Um lote do saldo: o mês, a categoria, em que pé está e as premiações dele. */
function BalancePartItem({
  part,
  highlighted,
}: {
  part: StatementBalancePart;
  /** O lote que se abriu pelo "Meu saldo" do topo. */
  highlighted: boolean;
}) {
  const awardsTotal =
    part.awards.length === 0
      ? null
      : sum(...part.awards.map((a) => fromColumn(a.amount)!));
  const amount = fromColumn(part.amount)!;

  return (
    <li
      aria-current={highlighted ? 'true' : undefined}
      className={cardClasses({
        density: 'densa',
        className: cn('flex flex-col gap-2', highlighted && 'ring-2 ring-foco'),
      })}
    >
      {highlighted && (
        <span className="self-start">
          <Badge tone="informacao">Você veio por este lote</Badge>
        </span>
      )}
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="font-semibold text-texto">Lote {part.category}</span>
        <span className="font-semibold tabular-nums text-texto">
          <MoneyValue value={part.amount} />
        </span>
      </div>

      <p className="text-sm text-texto-suave">{sourceSentence(part)}</p>

      {part.awards.length > 0 && (
        <dl className="grid grid-cols-[1fr_auto] gap-x-3 gap-y-1 text-sm">
          {part.awards.map((award) => (
            <div key={award.id} className="contents">
              <dt className="text-texto">{award.title}</dt>
              <dd className="text-right tabular-nums text-texto">
                <MoneyValue value={award.amount} />
              </dd>
            </div>
          ))}
        </dl>
      )}

      {/*
       * A nota já enviada é o valor da despesa, e ele pode não ser a soma das
       * premiações: a taxa de tecnologia do externo sai ali. A tela diz a
       * diferença em vez de deixar duas contas que não fecham.
       */}
      {part.source === 'no_financeiro' &&
        awardsTotal !== null &&
        awardsTotal !== amount && (
          <p className="text-xs text-texto-suave">
            As premiações somam {format(awardsTotal)}; o lote vale {format(amount)}, o
            valor da nota enviada — a diferença é o que foi descontado nela, como a taxa
            de tecnologia.
          </p>
        )}
    </li>
  );
}

/** Em que pé está o lote, numa frase com a data que importa. */
function sourceSentence(part: StatementBalancePart): string {
  if (part.source === 'competencia_aberta') {
    return closesAutomatically(part.period)
      ? `Competência aberta: o valor ainda pode crescer até o fechamento, em ${formatDate(competenceClosingDate(part.period))}.`
      : 'Competência aberta: o valor ainda pode crescer até alguém fechar o mês.';
  }
  if (part.source === 'aguardando_nota') {
    return 'Competência fechada. Falta enviar a nota fiscal deste lote para ele seguir ao Financeiro.';
  }
  const expense = part.expense;
  const sent = expense?.sentOn
    ? `Nota enviada em ${formatDate(expense.sentOn)}.`
    : 'Nota enviada.';
  if (expense === null) return sent;
  const stage = EXPENSE_STAGE_LABELS[expense.status as ExpenseStage] ?? expense.status;
  const due = expense.dueDate ? `, vencimento em ${formatDate(expense.dueDate)}` : '';
  return `${sent} No Financeiro: ${stage}${due}.`;
}

/**
 * Centavos, ou `null` quando a coluna não é legível.
 *
 * Só para decidir SINAL e COR. Quem escreve o número é `MoneyValue`, que diz
 * "Valor ilegível" em vez de desenhar um R$ 0,00 com cara de precisão — e é por
 * isso que aqui o ilegível cai no mesmo caminho do zero, sem sinal.
 */
function centsOf(value: string): number | null {
  try {
    return fromColumn(value);
  } catch {
    return null;
  }
}

/**
 * `2026-09-22` → `22 de setembro`.
 *
 * Sem o ano: a competência já está no filtro, no cartão do total e na faixa do
 * fechamento, e repeti-lo em cada cabeçalho de dia é ruído. Meio-dia UTC e
 * `HOUSE_TIMEZONE` pelo mesmo motivo de `formatDate`, do qual isto é a variante
 * longa: com meia-noite, o fuso puxa a data para o dia anterior em qualquer
 * lugar a oeste de Greenwich — o Brasil inteiro.
 */
function dayLabel(date: string): string {
  return new Intl.DateTimeFormat('pt-BR', {
    day: 'numeric',
    month: 'long',
    timeZone: HOUSE_TIMEZONE,
  }).format(new Date(`${date}T12:00:00Z`));
}
